import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import Row from '../../../core/layout/Row';
import Select from '../../../core/form/Select';
import Async from '../../../core/layout/Async';
import Button from '../../../core/form/Button';
import Column from '../../../core/layout/Column';
import TextInput from '../../../core/form/TextInput';
import Separator from '../../../core/layout/Separator';
import FormButtons from '../../../core/form/FormButtons';
import EditableTable from '../../../core/form/EditableTable';
import AudioPreview from '../../../core/layout/AudioPreview';
import LocalFileInputButton from '../../../core/form/LocalFileInputButton';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';

const InstructionAudioForm = (props) => {
  const handleUploadClick = (locale, fileExists, callback) => {
    if (fileExists) {
      ConfirmationDialogService.show(
        'Update Audio',
        `Would you like to change the audio file from locale ${locale}?`,
        () => callback()
      );
    } else {
      callback();
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        props.onSubmit();
      }}
    >
      <Row>
        <Column size={2}>
          <Select
            required
            label="Type"
            disabled={props.submitting || props.loading || get(props.values, 'id')}
            value={get(props.values, 'type', '')}
            onChange={(value) => props.onChange('type', value)}
            errorText={get(props.errors, 'type', null)}
            options={[
              {
                label: 'Student',
                value: 'STUDENT',
              },
              {
                label: 'Kids',
                value: 'KIDS',
              },
              {
                label: 'Parent',
                value: 'PARENT',
              },
              {
                label: 'Push Notifications',
                value: 'PUSH_NOTIFICATIONS',
              },
            ]}
          />
        </Column>
        <Column size={3}>
          <TextInput
            required
            label="Key"
            disabled={props.submitting || props.loading || get(props.values, 'id')}
            value={get(props.values, 'key', '')}
            onChange={(value) => props.onChange('key', value)}
            errorText={get(props.errors, 'key', null)}
          />
        </Column>
      </Row>
      <Row>
        <Async fetching={props.loading}>
          <EditableTable
            columns={[
              {
                label: 'Locale',
                path: 'locale',
                sort: true,
                defaultSortOrder: 'ascend',
                width: '80px',
              },
              {
                label: 'Text',
                path: 'text',
                editable: true,
              },
              {
                label: 'Audio Actions',
                width: '130px',
                render: (text, record) => {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {record.audio && (
                        <>
                          <AudioPreview src={record.audio} />
                          <Button
                            icon="download"
                            disabled={props.submitting}
                            onClick={() => props.onAudioDownload(record.audio)}
                          />
                        </>
                      )}
                      <LocalFileInputButton
                        disabled={props.submitting}
                        onClick={(callback) => handleUploadClick(record.locale, !!record.audio, callback)}
                        onChange={(value) => props.onChange(`audios[${record.locale}]`, value)}
                      />
                    </div>
                  );
                },
              },
            ]}
            actionsTitle="Text Actions"
            rows={get(props.values, 'instructions', [])}
            rowKey="locale"
            onSave={(row, change) => {
              const instructions = JSON.parse(JSON.stringify(get(props.values, 'instructions')));
              const instruction = instructions.find((instruction) => instruction.locale === row.locale);
              instruction.text = change.text.trim();
              props.onChange('instructions', instructions);
            }}
          />
        </Async>
      </Row>
      <Separator />
      <FormButtons
        confirmLabel={`${get(props.values, 'id') ? 'Update' : 'Create'} Instruction Audio`}
        isDisabled={props.submitting || props.loading}
        isSubmitting={props.submitting}
        onReset={props.onReset}
      />
    </form>
  );
};

InstructionAudioForm.propTypes = {
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  onAudioDownload: PropTypes.func,
  values: PropTypes.object,
  submitting: PropTypes.bool,
  loading: PropTypes.bool,
  errors: PropTypes.object,
};

InstructionAudioForm.defaultProps = {
  values: {},
  errors: {},
  submitting: false,
  loading: false,
  onSubmit: () => false,
  onReset: () => false,
  onAudioDownload: () => false,
};

export default InstructionAudioForm;
